import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  defer,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { JobInfo, JobType, ServerErrorResponse } from '@blaze-shared/api-contracts-domain';
import { isServerErrorResponse } from '@blaze-shared/platform-data-access';
import { countedShareReplay, isDeepEqual } from '@blaze-shared/shared-util';

import { JobsApiService } from './jobs-api.service';
import { isJobFinished } from './jobs.utils';

@Injectable({ providedIn: 'root' })
export class JobMonitorService {
  private readonly jobsApiService = inject(JobsApiService);

  private readonly pollIntervalMs = 2000;

  private readonly monitoredJobIds$$ = new BehaviorSubject<string[]>([]);
  private readonly latestJobsState$$ = new BehaviorSubject<Record<string, JobInfo<any>>>({});

  constructor() {
    this.monitoredJobIds$$
      .pipe(
        distinctUntilChanged(isDeepEqual),
        switchMap((jobIds) => {
          if (jobIds.length === 0) {
            return of([]);
          }

          return timer(0, this.pollIntervalMs).pipe(
            exhaustMap(() => {
              return this.jobsApiService.postJobsStatus(jobIds).pipe(
                catchError((err) => {
                  console.error('Job status polling failed', err);
                  return of(null);
                }),
              );
            }),
          );
        }),
      )
      .subscribe((jobs) => {
        if (!jobs) {
          return;
        }
        this.updateJobStates(jobs);
      });
  }

  observeJobState<T = unknown>(jobType: JobType): Observable<JobInfo<T>[]> {
    return this.latestJobsState$$.pipe(
      map((jobs) => Object.values(jobs).filter((job) => job?.type === jobType)),
      countedShareReplay(),
    );
  }

  observeJobCompletion<T = unknown>(jobType: JobType): Observable<JobInfo<T>[]> {
    return this.observeJobState<T>(jobType).pipe(
      map((jobs) => jobs.filter((state) => isJobFinished(state.status))),
      filter((jobs) => jobs.length > 0),
      distinctUntilChanged(isDeepEqual),
      countedShareReplay(),
    );
  }

  monitorJob<T = unknown>(jobId: string): Observable<JobInfo<T> | undefined> {
    const existingJob = this.latestJobsState$$.getValue()[jobId];
    if (!existingJob || !isJobFinished(existingJob.status)) {
      this.addJobToMonitor(jobId);
    }

    return this.latestJobsState$$.pipe(
      map((jobs) => jobs[jobId]),
      distinctUntilChanged(isDeepEqual),
      countedShareReplay(),
    );
  }

  addJobsToMonitor(jobIds: string[]): void {
    jobIds.forEach((jobId) => this.addJobToMonitor(jobId));
  }

  addJobToMonitor(jobId: string): void {
    const currentlyMonitoredIds = this.monitoredJobIds$$.getValue();
    if (!currentlyMonitoredIds.includes(jobId)) {
      this.monitoredJobIds$$.next([...currentlyMonitoredIds, jobId]);
    }
  }

  stopMonitoring(jobId: string | string[]): void {
    const toFilter = Array.isArray(jobId) ? jobId : [jobId];
    const currentIds = this.monitoredJobIds$$.getValue();
    const filteredIds = currentIds.filter((id) => !toFilter.includes(id));
    if (filteredIds.length !== currentIds.length) {
      this.monitoredJobIds$$.next(filteredIds);
    }
  }

  stopAll(): void {
    this.monitoredJobIds$$.next([]);
  }

  /**
   * Polls job status API for updates a long-running job until it completes or is stopped.
   * @param jobId
   * @param pollTime
   * @param cancelOnUnsubscribe
   * @param statusCallback
   * @returns
   */
  pollJobStatusUntilDoneOrStopped<T>(
    jobId?: string | null,
    pollTime = 5000,
    cancelOnUnsubscribe = false,
    statusCallback?: (info: JobInfo<T> | undefined) => void,
  ): Observable<T | ServerErrorResponse> {
    return defer(() => {
      const pollingComplete$$ = new BehaviorSubject(false);
      return timer(0, pollTime).pipe(
        takeUntil(pollingComplete$$.pipe(filter((complete) => complete))),
        exhaustMap(() => {
          return this.jobsApiService.postJobsStatus<T>(jobId ? [jobId] : []).pipe(
            map((jobStatuses) => {
              return jobStatuses.find((jobStatus) => jobStatus.id === jobId);
            }),
            tap((jobStatus) => {
              statusCallback?.(jobStatus);
            }),
            filter((jobStatus) => {
              return !!jobStatus && isJobFinished(jobStatus.status);
            }),
            tap(() => {
              pollingComplete$$.next(true);
            }),
            map((jobStatus) => {
              if (isServerErrorResponse(jobStatus?.result)) {
                return jobStatus?.result;
              }
              return jobStatus?.result as T;
            }),
          );
        }),
        finalize(() => {
          if (cancelOnUnsubscribe && !pollingComplete$$.value && jobId) {
            this.jobsApiService.postCancelJob(jobId).subscribe();
          }
        }),
      );
    });
  }

  private updateJobStates(jobs: JobInfo<unknown>[]): void {
    const currentStates: Record<string, JobInfo<unknown>> = {};
    const finishedJobIds: string[] = [];

    jobs.forEach((jobInfo) => {
      currentStates[jobInfo.id] = jobInfo;

      if (isJobFinished(jobInfo.status)) {
        finishedJobIds.push(jobInfo.id);
      }
    });

    this.latestJobsState$$.next(currentStates);

    if (finishedJobIds.length > 0) {
      const remainingIds = this.monitoredJobIds$$
        .getValue()
        .filter((id) => !finishedJobIds.includes(id));
      this.monitoredJobIds$$.next(remainingIds);
    }
  }
}
